import {
  Button,
  Checkbox,
  CheckboxTypography,
  Form,
  FormGrid,
  FormGridItem,
  InputLabel,
  Link,
  MenuItem,
  Select,
  type SelectProps,
  Stack,
  TextField,
  Typography,
  useEmailAddressValidator,
  useFirstNameValidator,
  useLastNameValidator,
  useMultilineTextValidator,
  usePhoneNumberValidator,
  useSelectValidator,
  useZipCodeValidator,
} from '@ace-clublabs/shared-component-library';
import { constants } from '@/constants';
import type { ContactInfo } from '@/features/appointments/utils/ContactInfo';
import type { Destination } from '@/features/destinations/utils/Destination';
import RSSearchBox from '@/features/relevanceSearch/components/RSSearchBox';
import { useRSSearchBox } from '@/features/relevanceSearch/hooks/useRSSearchBox';
import useContactInfoPrefill from '@/hooks/useContactInfoPrefill';
import useAppointmentStore from '@/store/AppointmentStore';
import { FlowType } from '@/types/FlowType';

export interface HandleFormSubmitData {
  selectedDestination: Destination;
  searchedDestination?: string;
  userSearch?: string;
}

interface EmailInquiryFormProps {
  onSubmit: (data: HandleFormSubmitData) => void;
}

type SelectOption = {
  label: string;
  value: string;
};

const typeOfTripOptions: SelectOption[] = [
  { label: 'Cruises', value: 'Cruise' },
  { label: 'Guided tours', value: 'Guided tour' },
  { label: 'Hotels', value: 'Hotel' },
  { label: 'Flights', value: 'Flight' },
  { label: 'Rental cars', value: 'Rental car' },
  { label: 'Building a vacation package', value: 'Build a vacation package' },
  { label: 'Day trips and activities', value: 'Day trips and activities' },
];

const typeOfTripOptionsMap: Record<string, string> = typeOfTripOptions.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {} as Record<string, string>,
);

const EmailInquiryForm: React.FC<EmailInquiryFormProps> = ({ onSubmit }) => {
  const formValues = useAppointmentStore((state) => state.contactInfo);
  const updateField = useAppointmentStore((state) => state.patchContactInfo);
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );

  const DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL = '0-3 Months';

  const firstNameValidator = useFirstNameValidator({ label: 'First name' });
  const lastNameValidator = useLastNameValidator({ label: 'Last name' });
  const zipCodeValidator = useZipCodeValidator({ label: 'ZIP code' });
  const selectValidator = useSelectValidator({
    label: 'How should we contact you?',
  });
  const phoneNumberValidator = usePhoneNumberValidator({
    label: 'Phone number',
    required: true,
  });
  const emailValidator = useEmailAddressValidator({
    label: 'Email',
    required: true,
  });
  const emailMessageValidator = useMultilineTextValidator({
    label: 'Email message',
    required: true,
  });

  const multiLineInputStyles = {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      border: 0,
      overflow: 'hidden',
    },
    '& .MuiTextField-root': {
      textAlign: 'left',
    },
    '& .MuiInputBase-inputMultiline': {
      border: '0 !important',
    },
  };

  const selectMenuProps: SelectProps['MenuProps'] = {
    sx: {
      '& .MuiMenu-paper': {
        maxHeight: {
          xs: 51 * 3,
          lg: 51 * 4,
        },
      },
    },
    PaperProps: { className: 'MuiSelect-Menu-paper' },
  };

  const { searchBoxProps, resolveDestination } = useRSSearchBox({
    flow: FlowType.EMAIL,
    formVariant: true,
  });

  useContactInfoPrefill({
    formValues,
    updateField,
  });

  const handleSubmit = () => {
    const resolvedDestination = resolveDestination();

    if (!resolvedDestination?.selectedDestination) return;

    const { selectedDestination, searchedDestination, userSearch } =
      resolvedDestination;

    setUserSearch(userSearch ?? '');
    setSearchedDestination(searchedDestination);
    setSelectedDestination(selectedDestination);

    onSubmit({
      selectedDestination,
      searchedDestination,
      userSearch,
    });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      sx={{
        margin: 0,
        padding: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
      }}
      data-testid='contactInfoForm'
    >
      <Stack
        direction='column'
        sx={{
          width: { lg: '65%', xs: '85%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: '8px',
          flex: 1,
          pb: 2,
          '& .MuiFormGrid-root': {
            width: '100%',
          },
        }}
      >
        <Stack
          direction='column'
          spacing={{
            xs: 0.5,
            md: 0,
          }}
          sx={{
            textAlign: {
              xs: 'left',
              md: 'center',
            },
          }}
        >
          <Typography data-testid='contactInfoHeader' variant='h4'>
            How should we contact you?
          </Typography>
        </Stack>
        <FormGrid
          sx={{
            width: '100%',
            marginTop: {
              xs: '20px',
              md: '32px',
            },
            textAlign: 'left',
            '& .MuiTextField-root': {
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          {/* First Name */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='firstName'
          >
            <TextField
              for='first-name'
              label='First name*'
              onChange={(e) => updateField({ firstName: e.target.value })}
              value={formValues.firstName}
              validator={firstNameValidator}
              data-testid='firstName'
            />
          </FormGridItem>

          {/* Last Name */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='lastName'
          >
            <TextField
              for='last-name'
              label='Last name*'
              onChange={(e) => updateField({ lastName: e.target.value })}
              value={formValues.lastName}
              validator={lastNameValidator}
              data-testid='lastName'
            />
          </FormGridItem>

          {/* Zip Code */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='zipCode'
          >
            <TextField
              for='zip-code'
              label='Zip code*'
              onChange={(e) => updateField({ zipCode: e.target.value })}
              validator={zipCodeValidator}
              value={formValues.zipCode}
              data-testid='zipCode'
              slotProps={{
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
          </FormGridItem>

          {/* Communication Preference */}
          <FormGridItem
            size={{ xs: 12, lg: 6 }}
            key='communicationPreference'
            data-testid='communicationPreferenceContainer'
          >
            <Select
              label='How should we contact you?'
              placeholder='Select one'
              onChange={(e) => {
                const newValue = e.target
                  .value as ContactInfo['communicationPreference'];
                if (!newValue) return;
                updateField({ communicationPreference: newValue });
              }}
              defaultValue={formValues.communicationPreference}
              validator={selectValidator}
              name='communicationPreference'
              fullWidth={true}
              data-testid='communicationPreference'
            >
              {(
                ['Phone', 'Email'] as NonNullable<
                  ContactInfo['communicationPreference']
                >[]
              ).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormGridItem>

          {/* Phone Number */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='phoneNumber'
          >
            <TextField
              for='phone-number'
              label='Phone number*'
              onChange={(e) => updateField({ phoneNumber: e.target.value })}
              validator={phoneNumberValidator}
              value={formValues.phoneNumber}
              data-testid='phoneNumber'
              slotProps={{
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
          </FormGridItem>

          {/* Email */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='email'
          >
            <TextField
              for='email-address'
              label='Email*'
              onChange={(e) => updateField({ email: e.target.value })}
              validator={emailValidator}
              value={formValues.email}
              data-testid='email'
            />
          </FormGridItem>

          {/* Expected Travel Time */}
          <FormGridItem
            size={{
              xs: 12,
              lg: 6,
            }}
            key='expectedTravelTime'
          >
            <Select
              label='When do you plan to travel'
              defaultValue={DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL}
              fullScreenThreshold={7}
              onChange={(e) =>
                updateField({ expectedTravelTime: e.target.value })
              }
              value={formValues.expectedTravelTime}
              name='expectedTravelTime'
              fullWidth={true}
              data-testid='expectedTravelTime'
              MenuProps={selectMenuProps}
            >
              {[
                DEFAULT_WHEN_DO_YOU_PLAN_TO_TRAVEL,
                '3-6 Months',
                '6-9 Months',
                '9-12 Months',
                '12+ Months',
                'Not Sure',
              ].map((travelTime) => (
                <MenuItem key={travelTime} value={travelTime}>
                  {travelTime}
                </MenuItem>
              ))}
            </Select>
          </FormGridItem>

          {/* Type of Trip */}
          <FormGridItem size={{ xs: 12, lg: 6 }} key='typeOfTrip'>
            <Select<string[]>
              label='What can we help you with?'
              placeholder='Types of trips'
              fullWidth
              multiple
              value={formValues.typeOfTrip || []}
              fullScreenThreshold={8}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return 'Types of trips';
                }
                return selected
                  .map((value) => {
                    const label = typeOfTripOptionsMap[value];
                    return label ? label : value;
                  })
                  .join(', ');
              }}
              onChange={(e) => {
                const value = e.target.value as string[];
                updateField({ typeOfTrip: value });
              }}
              name='typeOfTrip'
              data-testid='typeOfTrip'
              MenuProps={selectMenuProps}
            >
              {typeOfTripOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox
                    checked={formValues.typeOfTrip?.includes(value) || false}
                    variant='text'
                    label={
                      <CheckboxTypography
                        sx={{
                          fontSize: '16px',
                        }}
                      >
                        {label}
                      </CheckboxTypography>
                    }
                    value={value}
                    name={value}
                    slotProps={{
                      formControlLabelProps: {
                        sx: {
                          outline: 'none !important',
                          p: '0 !important',
                          pointerEvents: 'none',
                        },
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormGridItem>
          <FormGridItem size={12}>
            <InputLabel>Select a destination</InputLabel>
            <RSSearchBox {...searchBoxProps} formVariant />
          </FormGridItem>
          {/* Email Message */}
          <FormGridItem size={12} key='emailMessage'>
            <TextField
              name={'emailMessage'}
              data-testid='emailMessage'
              onChange={(e) => updateField({ emailMessage: e.target.value })}
              value={formValues.emailMessage}
              multiline
              fullWidth
              label={'Email message*'}
              placeholder={'Type a message to your advisor.'}
              validator={emailMessageValidator}
              sx={multiLineInputStyles}
            />
          </FormGridItem>
        </FormGrid>
        <Typography
          variant='b3Regular'
          sx={{
            textAlign: 'left',
            mt: 1,
          }}
        >
          By selecting "Submit", I agree to receive calls and texts, using
          automated technology, from or on AAA's behalf regarding membership
          products/services and offers. Consent is not a condition of purchase.
          I agree to the{' '}
          <Link href={constants.PRIVACY_NOTICE_URL} target='_blank'>
            Privacy Notice
          </Link>
          ,{' '}
          <Link href={constants.TERMS_AND_CONDITIONS_URL} target='_blank'>
            Terms & Conditions
          </Link>
          , and{' '}
          <Link href={constants.TEXT_TERMS_URL} target='_blank'>
            Text Terms
          </Link>
          .
        </Typography>
        <Button
          label='Submit'
          type='submit'
          sx={{
            mt: 2,
          }}
          fullWidth
        />
      </Stack>
    </Form>
  );
};

export default EmailInquiryForm;
